import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowUp, Check, ChevronRight, FileText } from 'lucide-react'
import { canNestUnder } from '../utils/docMove'
import { useMoveDoc } from '../hooks/useMoveDoc'
import type { ReactNode } from 'react'
import type { DocLocationPatch } from '../utils/docMove'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { usePages, useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { sortByRank } from '@/routes/features/spaces/utils/spaceDocs'
import { cn } from '@/lib/utils'

type Mode = { kind: 'root' } | { kind: 'space'; spaceId: string } | { kind: 'nest' }

interface MoveDocPopoverProps {
  page: PageDocument
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What the popover hangs from (the ⋯ button, or a table row's menu). */
  children: ReactNode
  /** The moved doc's new location — e.g. to follow it to its new URL. */
  onMoved?: (location: DocLocationPatch) => void
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
}

/**
 * "Move to…" (design 22b): promote a project doc to its space, move it to
 * another project or space, or nest it under another doc. Sub-pages move
 * with it; links and backlinks are by id so they keep working.
 */
export function MoveDocPopover({
  page,
  open,
  onOpenChange,
  children,
  onMoved,
  align = 'end',
  side = 'bottom',
}: MoveDocPopoverProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        align={align}
        side={side}
        className="w-[320px] max-w-[calc(100vw-32px)] overflow-hidden rounded-xl p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {open && <MoveDocPanel page={page} onDone={() => onOpenChange(false)} onMoved={onMoved} />}
      </PopoverContent>
    </Popover>
  )
}

function MoveDocPanel({
  page,
  onDone,
  onMoved,
}: {
  page: PageDocument
  onDone: () => void
  onMoved?: (location: DocLocationPatch) => void
}) {
  const { t } = useTranslation('projects')
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const { projects } = useProjects()
  const { pages } = usePages()
  const [mode, setMode] = useState<Mode>({ kind: 'root' })
  const [query, setQuery] = useState('')
  const moveDoc = useMoveDoc(pages)

  const live = useMemo(
    () => projects.filter((p) => !p.isDeleted && (!workspaceId || p.envId === workspaceId)),
    [projects, workspaceId],
  )
  const location = live.find((p) => p.id === page.projectId) ?? null
  const space = location
    ? location.parentProjectId
      ? live.find((p) => p.id === location.parentProjectId) ?? null
      : location
    : null
  const spaces = useMemo(
    () =>
      live
        .filter((p) => !p.parentProjectId)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [live],
  )
  const projectsOf = (spaceId: string) => sortByRank(live.filter((p) => p.parentProjectId === spaceId))

  const untitled = t('docs.untitled')
  const title = page.title || untitled

  const moveTo = (target: ProjectDocument, parentPageId: string | null = null, name = target.name) => {
    onDone()
    if (target.id === page.projectId && !parentPageId && !page.parentPageId) return
    void moveDoc(page.id, { projectId: target.id, parentPageId }, { destinationName: name, onMoved })
  }

  const resetTo = (next: Mode) => {
    setMode(next)
    setQuery('')
  }

  const locationRow = (target: ProjectDocument, opts: { isSpace: boolean }) => {
    const isCurrent = target.id === page.projectId
    const promote = opts.isSpace && target.id === space?.id && !isCurrent
    return (
      <CommandItem
        key={target.id}
        value={target.id}
        keywords={[target.name]}
        onSelect={() => moveTo(target)}
        className={cn('h-8 gap-2 rounded-md px-2', !opts.isSpace && 'pl-6')}
      >
        <LocationSwatch location={target} size={14} />
        <span className="min-w-0 flex-1 truncate">
          {opts.isSpace ? t('docs.move.spaceDoc', { name: target.name }) : target.name}
        </span>
        {promote && (
          <span className="flex shrink-0 items-center gap-0.5 text-[11.5px] text-muted-foreground">
            <ArrowUp className="size-3" />
            {t('docs.move.promote')}
          </span>
        )}
        {isCurrent && (
          <Check className="size-3.5 shrink-0 text-foreground" aria-label={t('docs.move.current')} />
        )}
      </CommandItem>
    )
  }

  const backRow = (
    <CommandItem value="__back" keywords={[t('docs.move.back')]} onSelect={() => resetTo({ kind: 'root' })} className="h-8 gap-2 rounded-md px-2 text-muted-foreground">
      <ArrowLeft className="size-3.5" />
      {t('docs.move.back')}
    </CommandItem>
  )

  let body: ReactNode
  if (mode.kind === 'nest') {
    const locationIds = space ? [space.id, ...projectsOf(space.id).map((p) => p.id)] : []
    const byId = new Map(pages.map((p) => [p.id, p]))
    const pathOf = (doc: PageDocument) => {
      const parts: Array<string> = []
      let parent = doc.parentPageId ? byId.get(doc.parentPageId) : undefined
      while (parent && parts.length < 4) {
        parts.unshift(parent.title || untitled)
        parent = parent.parentPageId ? byId.get(parent.parentPageId) : undefined
      }
      return parts.join(' / ')
    }
    body = (
      <>
        <CommandGroup>{backRow}</CommandGroup>
        {locationIds.map((id) => {
          const loc = live.find((p) => p.id === id)
          if (!loc) return null
          const candidates = pages
            .filter((p) => !p.isDeleted && p.projectId === id && canNestUnder(pages, page.id, p.id))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
          if (candidates.length === 0) return null
          return (
            <CommandGroup key={id} heading={loc.name}>
              {candidates.map((doc) => (
                <CommandItem
                  key={doc.id}
                  value={doc.id}
                  keywords={[doc.title || untitled, loc.name]}
                  onSelect={() => moveTo(loc, doc.id, doc.title || untitled)}
                  className="h-8 gap-2 rounded-md px-2"
                >
                  <FileText className="size-3.5" />
                  <span className="min-w-0 truncate">{doc.title || untitled}</span>
                  {doc.parentPageId && (
                    <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted-foreground">{pathOf(doc)}</span>
                  )}
                  {doc.id === page.parentPageId && <Check className="ml-auto size-3.5 shrink-0 text-foreground" />}
                </CommandItem>
              ))}
            </CommandGroup>
          )
        })}
      </>
    )
  } else if (mode.kind === 'space') {
    const other = live.find((p) => p.id === mode.spaceId)
    body = other ? (
      <>
        <CommandGroup>{backRow}</CommandGroup>
        <CommandGroup>
          {locationRow(other, { isSpace: true })}
          {projectsOf(other.id).map((p) => locationRow(p, { isSpace: false }))}
        </CommandGroup>
      </>
    ) : null
  } else {
    const otherSpaces = spaces.filter((s) => s.id !== space?.id)
    body = (
      <>
        {space && (
          <CommandGroup>
            {locationRow(space, { isSpace: true })}
            {projectsOf(space.id).map((p) => locationRow(p, { isSpace: false }))}
            <CommandItem
              value="__nest"
              keywords={[t('docs.move.nest')]}
              onSelect={() => resetTo({ kind: 'nest' })}
              className="h-8 gap-2 rounded-md px-2 pl-6 text-muted-foreground"
            >
              <FileText className="size-3.5" />
              <span className="flex-1 truncate">{t('docs.move.nest')}</span>
            </CommandItem>
          </CommandGroup>
        )}
        {otherSpaces.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup>
              {otherSpaces.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.id}
                  keywords={[s.name]}
                  onSelect={() => resetTo({ kind: 'space', spaceId: s.id })}
                  className="h-8 gap-2 rounded-md px-2"
                >
                  <LocationSwatch location={s} size={14} />
                  <span className="min-w-0 flex-1 truncate">{t('docs.move.otherSpace', { name: s.name })}</span>
                  <ChevronRight className="size-3.5" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </>
    )
  }

  return (
    <Command
      className="bg-transparent"
      filter={(_value, search, keywords) =>
        (keywords ?? []).join(' ').toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
      }
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={
          mode.kind === 'nest'
            ? t('docs.move.nestPlaceholder', { title })
            : t('docs.move.placeholder', { title })
        }
        className="text-[13px]"
      />
      <CommandList className="max-h-[320px] p-1">
        <CommandEmpty className="px-3 py-6 text-center text-[13px] text-muted-foreground">
          {t('docs.move.empty')}
        </CommandEmpty>
        {body}
      </CommandList>
      <p className="border-t px-3 py-2.5 text-[11.5px] leading-snug text-muted-foreground">
        {t('docs.move.footer')}
      </p>
    </Command>
  )
}
